import React from 'react';
import { Box, Typography } from '@mui/material';
import { calculateLineItemTotals } from '../../../utils/taxCalculations';

function formatMoney(amount, currency = 'GBP') {
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency }).format(amount);
}

export function getProductPriceTotals({ price, taxPercentage, priceIncludesTax }) {
  const unitPrice = parseFloat(price);
  if (!Number.isFinite(unitPrice) || unitPrice < 0) {
    return { subtotal: 0, taxAmount: 0, total: 0, valid: false };
  }
  const totals = calculateLineItemTotals({
    quantity: 1,
    unitPrice,
    taxPercentage: parseFloat(taxPercentage) || 0,
    priceIncludesTax: Boolean(priceIncludesTax)
  });
  return { ...totals, valid: true };
}

/**
 * @param {'form' | 'table'} variant — form: full breakdown card; table: compact stack for list rows
 */
function ProductPriceBreakdown({
  price,
  taxPercentage,
  priceIncludesTax,
  currency = 'GBP',
  variant = 'table'
}) {
  const taxPct = parseFloat(taxPercentage) || 0;
  const { subtotal, taxAmount, total, valid } = getProductPriceTotals({
    price,
    taxPercentage,
    priceIncludesTax
  });

  if (!valid) {
    return variant === 'form' ? null : (
      <Typography variant="body2" color="text.secondary">—</Typography>
    );
  }

  if (variant === 'form') {
    return (
      <Box
        sx={{
          mt: 2,
          p: 2,
          borderRadius: 1,
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper'
        }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
          Price breakdown
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
          <Typography variant="body2" color="text.secondary">
            Subtotal (excl. VAT)
          </Typography>
          <Typography variant="body2">{formatMoney(subtotal, currency)}</Typography>
        </Box>
        {taxPct > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
            <Typography variant="body2" color="text.secondary">
              VAT ({taxPct}%)
            </Typography>
            <Typography variant="body2">{formatMoney(taxAmount, currency)}</Typography>
          </Box>
        )}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            pt: 1,
            mt: 0.5,
            borderTop: '1px solid',
            borderColor: 'divider'
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            Total
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            {formatMoney(total, currency)}
          </Typography>
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
          Price entered {priceIncludesTax ? 'incl.' : 'excl.'} VAT
          {taxPct === 0 ? ' (no VAT rate set)' : ''}
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="body2" sx={{ fontWeight: 700 }}>
        {formatMoney(total, currency)}
      </Typography>
      {taxPct > 0 ? (
        <Typography variant="caption" color="text.secondary" display="block">
          {formatMoney(subtotal, currency)} + {formatMoney(taxAmount, currency)} VAT
        </Typography>
      ) : (
        <Typography variant="caption" color="text.secondary" display="block">
          No VAT
        </Typography>
      )}
    </Box>
  );
}

export default ProductPriceBreakdown;
